import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import en from './en';
import uk from './uk';
import ru from './ru';
import serviceMedia, { publishedServiceSlugs } from '../data/services';

const dictionaries = { uk, ru, en };
const list = [uk, ru, en];
const STORAGE_KEY = 'pearl_clean_lang';

const LanguageContext = createContext({
  code: 'uk',
  setCode: () => {},
  t: (key) => key,
  languages: list,
  services: []
});

const hydrateServices = (dict) => {
  const map = new Map(serviceMedia.map((s) => [s.slug, s]));
  return (dict.services || []).filter((svc) => publishedServiceSlugs.has(svc.slug)).map((svc) => ({
    ...svc,
    ...(map.get(svc.slug) || {})
  }));
};

const resolve = (obj, path) => path.split('.').reduce((acc, segment) => (acc ? acc[segment] : undefined), obj);

export const LanguageProvider = ({ children }) => {
  const [code, setCode] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    return saved && dictionaries[saved] ? saved : 'uk';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, code);
    document.documentElement.lang = code;
  }, [code]);

  const value = useMemo(() => {
    const dictionary = dictionaries[code] || uk;
    const t = (key, fallback) => {
      const found = resolve(dictionary, key);
      return found ?? fallback ?? key;
    };
    return {
      code,
      setCode,
      t,
      languages: list,
      services: hydrateServices(dictionary),
      dictionary
    };
  }, [code]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useI18n = () => useContext(LanguageContext);
